import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { http, formatDetail, auDateTime } from "./api";

export default function MessagesTab({ engagementId }) {
  const [msgs, setMsgs] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    http.get(`/portal/client/engagements/${engagementId}/messages`)
      .then(({ data }) => setMsgs(data))
      .catch(() => toast.error("Could not load messages."));
  }, [engagementId]);
  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await http.post(`/portal/client/engagements/${engagementId}/messages`, { body });
      setBody("");
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="messages-tab">
      <h2 className="font-serif text-2xl mb-4">Secure messages</h2>
      <p data-testid="messages-warning" className="text-sm text-bone leading-relaxed border-l-2 border-ember pl-4 mb-6">
        Do not place restricted cultural knowledge or highly sensitive evidence in messages. Use the approved secure evidence process.
      </p>
      {msgs === null ? (
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-stone2">Loading…</p>
      ) : (
        <div className="space-y-3 mb-6" data-testid="messages-list">
          {msgs.length === 0 && <p className="text-sm text-stone2 border border-line bg-surface1 p-6">No messages yet. Start the conversation below.</p>}
          {msgs.map((m, i) => (
            <div key={m.id} data-testid={`message-${i}`} className={`max-w-2xl p-4 border ${m.sender_role === "admin" ? "border-ember/40 bg-surface1" : "border-line bg-ink ml-auto"}`}>
              <div className="flex items-baseline justify-between gap-4 mb-1.5">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre">{m.sender_role === "admin" ? "Bigibila" : m.sender_name}</span>
                <time className="font-mono text-[10px] text-stone2">{auDateTime(m.created_at)}</time>
              </div>
              <p className="text-sm text-stone2 whitespace-pre-wrap">{m.body}</p>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={send} className="flex gap-3 items-end">
        <textarea data-testid="message-input" rows={3} className="field-dark flex-1 px-4 py-3 text-sm" placeholder="Message Bigibila about engagement administration or clarification…" value={body} onChange={(e) => setBody(e.target.value)} maxLength={5000} />
        <button type="submit" disabled={busy || !body.trim()} data-testid="send-message-btn" className="font-mono text-xs uppercase tracking-[0.15em] px-5 py-3.5 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 disabled:opacity-50 flex items-center gap-2">
          <Send size={14} /> Send
        </button>
      </form>
    </div>
  );
}
